import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { addDays, format, parseISO, startOfToday } from 'date-fns';
import MagnifyingGlassSvg from '../../../assets/svg-fc/MagnifyingGlassSvg';
import PhCaretDownSvg from '../../../assets/svg-fc/PhCaretDownSvg';
import PhCheckSvg from '../../../assets/svg-fc/PhCheckSvg';
import PhXSvg from '../../../assets/svg-fc/PhXSvg';
import { UI_TOKENS } from '../../../constants/designTokens';
import { unzoomed, useUiZoom } from '../../../context/UiZoomContext';
import PhArrowDownBoldSvg from '../../../assets/svg-fc/PhArrowDownBoldSvg';
import PhArrowsDownUpSvg from '../../../assets/svg-fc/PhArrowsDownUpSvg';
import PhArrowUpBoldSvg from '../../../assets/svg-fc/PhArrowUpBoldSvg';
import PhFunnelSimpleSvg from '../../../assets/svg-fc/PhFunnelSimpleSvg';
import PhPlusSvg from '../../../assets/svg-fc/PhPlusSvg';
import PhColumnsSvg from '../../../assets/svg-fc/PhColumnsSvg';
import ColumnsMenu from './ColumnsMenu';
import { matchesSearch } from '../../../utils/textSearch';

export type SortDirection = 'asc' | 'desc';

export interface iCaterersFilterOption {
  label: string;
  count: number;
}

/** `yyyy-MM-dd` bounds, either may be open. */
export interface iDateRange {
  from?: string;
  to?: string;
}

export interface iTableFilter {
  key: string;
  label: string;
  icon: ReactNode;
  kind?: 'options' | 'dateRange';
  range?: iDateRange;
  /** Active filters only — options with how many caterers match each, given the other filters. */
  options: iCaterersFilterOption[];
  /** Free lists (cuisines, emirates) get a search box once they run long; fixed buckets keep their order. */
  searchable: boolean;
  selected: string[];
}

export interface iTableSort {
  key: string;
  label: string;
  asc: string;
  desc: string;
}

interface iTableToolbarProps {
  search: string;
  onSearch: (value: string) => void;
  /** Every filter the directory offers; `activeKeys` are the pills shown. */
  filters: iTableFilter[];
  activeKeys: string[];
  onAddFilter: (key: string) => void;
  onRemoveFilter: (key: string) => void;
  onToggleOption: (key: string, value: string) => void;
  onSetRange?: (key: string, range: iDateRange) => void;
  onClearFilter: (key: string) => void;
  onClearAll: () => void;
  rules?: { id: string; label: string }[];
  onRemoveRule?: (id: string) => void;
  /** Called when a popover closes — pills left without a selection drop out (prototype `dirCloseMenu`). */
  onMenuClose: () => void;
  sorts: iTableSort[];
  sortKey: string;
  sortDirection: SortDirection;
  onSortKey: (key: string) => void;
  onSortDirection: (direction: SortDirection) => void;
  leading?: ReactNode;
  filterActions?: ReactNode;
  searchPlacement?: 'start' | 'end';
  searchPlaceholder?: string;
  searchSlot?: ReactNode;
  columns?: { key: string; label: string }[];
  hiddenColumns?: string[];
  onToggleColumn?: (key: string) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  pinnedColumns?: string[];
  onTogglePin?: (key: string) => void;
  padding?: string;
  compact?: boolean;
}

const popoverSx = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  bgcolor: '#fff',
  border: `1px solid ${UI_TOKENS.borderDefault}`,
  borderRadius: '10px',
  boxShadow: '0 12px 32px rgba(34,3,31,0.12)',
  zIndex: 42,
  overflow: 'hidden',
  fontFamily: UI_TOKENS.fontSans,
  maxWidth: `calc(${unzoomed('100vw')} - 32px)`,
} as const;

const toolButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: 34,
  padding: '0 10px',
  border: 'none',
  borderRadius: UI_TOKENS.radiusPill,
  background: 'transparent',
  fontFamily: UI_TOKENS.fontSans,
  fontSize: 13,
  color: UI_TOKENS.textSecondary,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: UI_TOKENS.grey50, color: UI_TOKENS.textStrong },
} as const;

const menuPaperSx = {
  display: 'flex',
  flexDirection: 'column',
  bgcolor: '#fff',
  border: `1px solid ${UI_TOKENS.borderDefault}`,
  borderRadius: '10px',
  boxShadow: '0 12px 32px rgba(34,3,31,0.12)',
  overflow: 'hidden',
} as const;

const SCREEN_GAP = 12;
/**
 * A menu never gets shorter than this (about three options under the search): it always opens below its button, and
 * with less room than this the page scrolls to the rest.
 */
const MIN_MENU_HEIGHT = 240;

/**
 * Keeps the open toolbar menu on screen: its height is capped to the room below it (its list scrolls inside), and it moves back in when it would run off the left or right edge. Rects are on screen; the zoomed menu's lengths are in zoomed px.
 */
const menuRoom = (anchor: HTMLElement | null) => {
  if (!anchor) return { placement: 'bottom-end' as const, maxHeight: 320 };
  const rect = anchor.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - 6 - SCREEN_GAP;
  return { placement: 'bottom-end' as const, maxHeight: Math.max(120, below) };
};

const useFitOnScreen = (openKey: string | null) => {
  const zoom = useUiZoom();
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [fit, setFit] = useState<{ maxHeight?: number; shift: number; up: boolean }>({ shift: 0, up: false });
  const shiftRef = useRef(0);

  useLayoutEffect(() => {
    shiftRef.current = 0;
    setFit({ shift: 0, up: false });
    if (!openKey || !node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const scale = node.offsetHeight ? rect.height / node.offsetHeight : zoom;
      // The trigger — the menu's positioned parent — so the menu's own height doesn't feed back into the measurement.
      const anchor = (node.offsetParent ?? node).getBoundingClientRect();
      // Where the menu sits without our shift, so re-measuring doesn't compound it.
      const applied = shiftRef.current * scale;
      const left = rect.left - applied;
      const right = rect.right - applied;
      const overRight = right - (window.innerWidth - SCREEN_GAP);
      const card = node.closest('[data-table-card]')?.getBoundingClientRect();
      const overLeft = Math.max(SCREEN_GAP, card ? card.left : 0) - left;
      let shift = 0;
      if (overLeft > 0) shift = overLeft / scale;
      else if (overRight > 0) shift = -Math.min(overRight, left - SCREEN_GAP) / scale;
      const gap = 6 * scale;
      const below = window.innerHeight - anchor.bottom - gap - SCREEN_GAP;
      const min = MIN_MENU_HEIGHT * scale;
      shiftRef.current = shift;
      setFit({ maxHeight: Math.max(min, below) / scale, shift, up: false });
    };
    measure();
    window.addEventListener('resize', measure);
    // The page scrolls under an open menu (capture: the scroller is the page's own content box).
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [openKey, node, zoom]);

  const ref = useCallback((element: HTMLElement | null) => setNode(element), []);
  const sx = {
    display: 'flex',
    flexDirection: 'column',
    // Only a menu's own scroll list gives way when it's capped.
    '& > :not(.yc-scroll)': { flexShrink: 0 },
    ...(fit.maxHeight !== undefined && { maxHeight: fit.maxHeight }),
    ...(fit.shift !== 0 && { transform: `translateX(${fit.shift}px)` }),
    ...(fit.up && { top: 'auto', bottom: 'calc(100% + 6px)' }),
  } as const;
  return { ref, sx };
};

const menuItemSx = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: '10px',
  height: 36,
  padding: '0 10px',
  border: 'none',
  background: 'transparent',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: UI_TOKENS.fontSans,
  fontSize: 13,
  color: UI_TOKENS.textStrong,
  textAlign: 'left',
  '&:hover': { bgcolor: UI_TOKENS.grey50 },
} as const;

const menuHeadingSx = { padding: '6px 10px', fontSize: 12, color: UI_TOKENS.textSecondary };

const formatDay = (value: string) => format(parseISO(value), 'd MMM yyyy');

const formatRange = ({ from, to }: iDateRange) =>
  from && to
    ? from === to
      ? formatDay(from)
      : `${formatDay(from)} – ${formatDay(to)}`
    : from
    ? `From ${formatDay(from)}`
    : to
    ? `Until ${formatDay(to)}`
    : '';

const dateInputSx = {
  height: 30,
  width: '100%',
  padding: '0 10px',
  border: `1px solid ${UI_TOKENS.borderDefault}`,
  borderRadius: '8px',
  outline: 'none',
  fontFamily: UI_TOKENS.fontSans,
  fontSize: 13,
  color: UI_TOKENS.textStrong,
  '&:focus': { borderColor: UI_TOKENS.sage600 },
};

const Check: FC<{ on: boolean }> = ({ on }) => (
  <Box component="span" sx={{ display: 'inline-flex', color: UI_TOKENS.sage700, opacity: on ? 1 : 0 }}>
    <PhCheckSvg size={13} />
  </Box>
);

const CREATED_SORT = { asc: 'Oldest first', desc: 'Newest first' };

const DATE_PRESETS = [
  { id: 'last7', label: 'Last 7 days', from: -7, to: 0 },
  { id: 'last30', label: 'Last 30 days', from: -30, to: 0 },
  { id: 'next7', label: 'Next 7 days', from: 0, to: 7 },
  { id: 'next30', label: 'Next 30 days', from: 0, to: 30 },
];

const Radio: FC<{ on: boolean }> = ({ on }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 18,
      height: 18,
      flexShrink: 0,
      boxSizing: 'border-box',
      borderRadius: '50%',
      border: `1.5px solid ${on ? UI_TOKENS.sage600 : UI_TOKENS.borderDefault}`,
    }}
  >
    {on && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: UI_TOKENS.sage600 }} />}
  </Box>
);

const Checkbox: FC<{ on: boolean }> = ({ on }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      borderRadius: '4px',
      flexShrink: 0,
      bgcolor: on ? UI_TOKENS.sage600 : '#fff',
      border: `1px solid ${on ? UI_TOKENS.sage600 : UI_TOKENS.grey300}`,
      color: '#fff',
      '& svg': { opacity: on ? 1 : 0 },
    }}
  >
    <PhCheckSvg size={10} />
  </Box>
);

const TableToolbar: FC<iTableToolbarProps> = ({
  search,
  onSearch,
  filters,
  activeKeys,
  onAddFilter,
  onRemoveFilter,
  onToggleOption,
  onSetRange,
  onClearAll,
  rules = [],
  onRemoveRule,
  onMenuClose,
  sorts,
  sortKey,
  sortDirection,
  onSortKey,
  onSortDirection,
  leading,
  filterActions,
  searchPlacement = 'start',
  searchPlaceholder = 'Search caterers',
  searchSlot,
  columns = [],
  hiddenColumns = [],
  onToggleColumn,
  columnOrder,
  onColumnOrderChange,
  pinnedColumns,
  onTogglePin,
  padding = '14px 20px',
  compact: compactProp = false,
}) => {
  const narrow = useMediaQuery('(max-width:1023.98px)');
  const compact = compactProp || narrow;
  const [menu, setMenu] = useState<string | null>(null);
  const [optionQuery, setOptionQuery] = useState('');
  const [customKey, setCustomKey] = useState<string | null>(null);
  const menuFit = useFitOnScreen(menu);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const anchorZoom = (anchor: HTMLElement | null) =>
    anchor?.offsetHeight ? anchor.getBoundingClientRect().height / anchor.offsetHeight : 1;
  const byKey = Object.fromEntries(filters.map((filter) => [filter.key, filter]));
  const addable = filters.filter((filter) => !activeKeys.includes(filter.key));
  const hasFilters = activeKeys.length > 0 || rules.length > 0;
  const currentSort = sorts.find((item) => item.key === sortKey);
  const sort = currentSort ?? CREATED_SORT;

  const openMenu = (key: string) => {
    setOptionQuery('');
    setMenu((current) => (current === key ? null : key));
  };

  const closeMenu = () => {
    setMenu(null);
    setOptionQuery('');
    onMenuClose();
  };
  const closeMenuRef = useRef(closeMenu);
  closeMenuRef.current = closeMenu;

  // A press anywhere outside the open menu and its trigger closes it (MAK-1065), as does Escape. A listener rather
  // than a fixed backdrop: the glass cards around the toolbar use backdrop-filter, which confines a fixed element to
  // the card, so a backdrop never covered the rest of the page.
  useEffect(() => {
    if (!menu) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.(`[data-toolbar-menu="${menu}"]`)) return;
      closeMenuRef.current();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenuRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menu]);

  const searchBox = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: compact ? 40 : 34,
        padding: '0 12px',
        mr: searchPlacement === 'start' ? '4px' : 0,
        border: `1px solid ${UI_TOKENS.borderDefault}`,
        borderRadius: '8px',
        boxSizing: 'border-box',
        flexShrink: compact ? 1 : 0,
        ...(compact && { flex: 1, minWidth: 0 }),
        bgcolor: '#fff',
        '&:focus-within': { borderColor: UI_TOKENS.sage600 },
      }}
    >
      <Box sx={{ display: 'flex', color: UI_TOKENS.textMuted, '& svg': { width: 15, height: 15 } }}>
        <MagnifyingGlassSvg />
      </Box>
      <Box
        component="input"
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label="Search"
        sx={{
          width: compact ? '100%' : { xs: 140, sm: 200 },
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: UI_TOKENS.fontSans,
          fontSize: 13,
          color: UI_TOKENS.textStrong,
        }}
      />
    </Box>
  );

  const appliedCount =
    rules.length +
    activeKeys.filter((key) => {
      const filter = byKey[key];
      if (!filter) return false;
      return filter.kind === 'dateRange' ? Boolean(filter.range?.from || filter.range?.to) : filter.selected.length > 0;
    }).length;
  const chipHeight = compact ? 32 : 26;

  const addMenu = (
    <Box ref={menuFit.ref} sx={{ ...popoverSx, left: 0, width: compact ? 220 : 240, padding: '6px', ...menuFit.sx, overflowY: 'auto' }}>
      <Box sx={menuHeadingSx}>Filter by</Box>
      {addable.map((filter) => (
        <Box
          key={filter.key}
          component="button"
          type="button"
          onClick={() => {
            setOptionQuery('');
            setMenu(filter.key);
            onAddFilter(filter.key);
          }}
          sx={menuItemSx}
        >
          <Box component="span" sx={{ display: 'inline-flex', color: UI_TOKENS.textSecondary }}>
            {filter.icon}
          </Box>
          <Box component="span" sx={{ flex: 1 }}>
            {filter.label}
          </Box>
        </Box>
      ))}
      {addable.length === 0 && (
        <Box sx={{ padding: '8px 10px', fontSize: 13, color: UI_TOKENS.textMuted }}>All filters are in use.</Box>
      )}
    </Box>
  );

  const countBadgeSx = compact
    ? {
        position: 'absolute' as const,
        top: 3,
        right: 1,
        minWidth: 16,
        height: 16,
        padding: '0 4px',
        fontSize: 10,
        border: '1.5px solid #fff',
      }
    : { minWidth: 20, height: 20, padding: '0 6px', fontSize: 12 };

  const filterButton = (
    <Box data-toolbar-menu="__add" sx={{ position: 'relative', flexShrink: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={() => openMenu('__add')}
        aria-label="Filter"
        title={compact ? 'Filter' : undefined}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: compact ? 40 : 34,
          width: compact ? 40 : undefined,
          padding: compact ? 0 : '0 14px 0 12px',
          border: compact ? 'none' : `1px solid ${UI_TOKENS.borderDefault}`,
          borderRadius: UI_TOKENS.radiusPill,
          boxSizing: 'border-box',
          bgcolor: menu === '__add' ? (compact ? UI_TOKENS.grey50 : '#F6F6F6') : compact ? 'transparent' : '#fff',
          fontFamily: UI_TOKENS.fontSans,
          fontSize: 13,
          fontWeight: 500,
          color: UI_TOKENS.textStrong,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: '#F6F6F6' },
        }}
      >
        <PhFunnelSimpleSvg size={compact ? 20 : 15} />
        {!compact && 'Filter'}
        {appliedCount > 0 && (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              borderRadius: UI_TOKENS.radiusPill,
              bgcolor: UI_TOKENS.sage600,
              color: '#fff',
              fontWeight: 500,
              ...countBadgeSx,
            }}
          >
            {appliedCount}
          </Box>
        )}
      </Box>
      {menu === '__add' && addMenu}
    </Box>
  );

  const iconButtonSx = (open: boolean) => ({
    position: 'relative' as const,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    padding: 0,
    flexShrink: 0,
    border: 'none',
    borderRadius: UI_TOKENS.radiusPill,
    bgcolor: open ? UI_TOKENS.grey50 : 'transparent',
    color: open ? UI_TOKENS.textStrong : UI_TOKENS.textSecondary,
    cursor: 'pointer',
  });

  const revealRange = useCallback((element: HTMLElement | null) => element?.scrollIntoView({ block: 'nearest' }), []);

  const presetsFor = (key: string, range: iDateRange) => {
    const today = startOfToday();
    const day = (offset: number) => format(addDays(today, offset), 'yyyy-MM-dd');
    const presets = DATE_PRESETS.map((preset) => ({ ...preset, from: day(preset.from), to: day(preset.to) }));
    const matched = presets.find((preset) => preset.from === range.from && preset.to === range.to);
    const custom = customKey === key || (!matched && Boolean(range.from || range.to));
    return { presets, matched, custom };
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          zIndex: 41,
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '2px' : '12px',
          flexWrap: compact ? 'nowrap' : 'wrap',
          padding: compact ? '12px' : padding,
          minWidth: 0,
          fontFamily: UI_TOKENS.fontSans,
        }}
      >
        {leading && (
          <>
            {leading}
            <Box
              component="span"
              sx={{ width: '1px', height: 20, mx: '4px', bgcolor: UI_TOKENS.borderDefault, flexShrink: 0 }}
            />
          </>
        )}
        {searchPlacement === 'start' && (searchSlot ?? searchBox)}

        {filterButton}

        {!compact && <Box component="span" sx={{ flex: 1 }} />}

        {searchPlacement === 'end' && (searchSlot ?? searchBox)}

        {!compact && columns.length > 0 && onToggleColumn && (
          <Box data-toolbar-menu="__columns" sx={{ position: 'relative' }}>
            <Box
              ref={setColumnsAnchor}
              component="button"
              type="button"
              onClick={() => openMenu('__columns')}
              sx={{ ...toolButtonSx, ...(menu === '__columns' && { bgcolor: UI_TOKENS.grey50, color: UI_TOKENS.textStrong }) }}
            >
              <PhColumnsSvg size={14} />
              Columns
            </Box>
            <Popper
              open={menu === '__columns' && Boolean(columnsAnchor)}
              anchorEl={columnsAnchor}
              placement={menuRoom(columnsAnchor).placement}
              modifiers={[{ name: 'flip', enabled: false }]}
              sx={{ zIndex: 1300 }}
            >
              <Box sx={{ ...menuPaperSx, my: '6px', maxHeight: menuRoom(columnsAnchor).maxHeight }}>
                <Box data-toolbar-menu="__columns" className="yc-scroll" sx={{ zoom: anchorZoom(columnsAnchor), overflowY: 'auto' }}>
                  <ColumnsMenu
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onToggleColumn={onToggleColumn}
                    columnOrder={columnOrder}
                    onColumnOrderChange={onColumnOrderChange}
                    pinnedColumns={pinnedColumns}
                    onTogglePin={onTogglePin}
                  />
                </Box>
              </Box>
            </Popper>
          </Box>
        )}

        {sorts.length > 0 && (
          <Box data-toolbar-menu="__sort" sx={{ position: 'relative' }}>
            <Box
              ref={setSortAnchor}
              component="button"
              type="button"
              onClick={() => openMenu('__sort')}
              aria-label={currentSort ? `Sorted by ${currentSort.label}, ${sortDirection === 'asc' ? sort.asc : sort.desc}` : 'Sort'}
              title={currentSort ? `${currentSort.label} · ${sortDirection === 'asc' ? sort.asc : sort.desc}` : compact ? 'Sort' : undefined}
              sx={
                compact
                  ? iconButtonSx(menu === '__sort')
                  : { ...toolButtonSx, ...(menu === '__sort' && { bgcolor: UI_TOKENS.grey50, color: UI_TOKENS.textStrong }) }
              }
            >
              <PhArrowsDownUpSvg size={compact ? 19 : 14} />
              {!compact && 'Sort'}
              {currentSort && (
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: UI_TOKENS.sage600,
                    ...(compact && { position: 'absolute', top: 8, right: 7 }),
                  }}
                />
              )}
            </Box>
            <Popper
              open={menu === '__sort' && Boolean(sortAnchor)}
              anchorEl={sortAnchor}
              placement={menuRoom(sortAnchor).placement}
              modifiers={[{ name: 'flip', enabled: false }]}
              sx={{ zIndex: 1300 }}
            >
              <Box sx={{ ...menuPaperSx, my: '6px', maxHeight: menuRoom(sortAnchor).maxHeight }}>
                <Box
                  data-toolbar-menu="__sort"
                  sx={{
                    zoom: anchorZoom(sortAnchor),
                    width: compact ? 240 : 260,
                    minHeight: 0,
                    padding: '6px',
                    overflowY: 'auto',
                    fontFamily: UI_TOKENS.fontSans,
                  }}
                >
                  <Box sx={{ padding: '6px 6px 8px' }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '2px',
                        padding: '3px',
                        bgcolor: UI_TOKENS.grey50,
                        borderRadius: '8px',
                      }}
                    >
                      {(['desc', 'asc'] as SortDirection[]).map((direction) => {
                        const on = direction === sortDirection;
                        return (
                          <Box
                            key={direction}
                            component="button"
                            type="button"
                            onClick={() => onSortDirection(direction)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: 30,
                              padding: '0 6px',
                              whiteSpace: 'nowrap',
                              border: 'none',
                              borderRadius: '6px',
                              bgcolor: on ? '#fff' : 'transparent',
                              boxShadow: on ? '0 1px 2px rgba(16,24,40,0.12)' : 'none',
                              fontFamily: UI_TOKENS.fontSans,
                              fontSize: 12.5,
                              fontWeight: 500,
                              color: on ? UI_TOKENS.textStrong : UI_TOKENS.textSecondary,
                              cursor: 'pointer',
                            }}
                          >
                            {direction === 'asc' ? <PhArrowUpBoldSvg size={13} /> : <PhArrowDownBoldSvg size={13} />}
                            {direction === 'asc' ? sort.asc : sort.desc}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  <Box sx={menuHeadingSx}>Sort by</Box>
                  {sorts.map((item) => (
                    <Box
                      key={item.key}
                      component="button"
                      type="button"
                      onClick={() => onSortKey(item.key === currentSort?.key ? '' : item.key)}
                      sx={menuItemSx}
                    >
                      <Box component="span" sx={{ flex: 1 }}>
                        {item.label}
                      </Box>
                      <Check on={item.key === currentSort?.key} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Popper>
          </Box>
        )}
      </Box>

      {hasFilters && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 40,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: compact ? '0 12px 12px' : '0 20px 14px',
            minWidth: 0,
            fontFamily: UI_TOKENS.fontSans,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexWrap: compact ? 'nowrap' : 'wrap',
              alignItems: 'center',
              gap: '8px',
              overflowX: compact ? 'auto' : 'visible',
              scrollbarWidth: 'none',
            }}
          >
        {rules.map((rule) => (
          <Box
            key={`rule-${rule.id}`}
            title={rule.label}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              height: chipHeight,
              maxWidth: '100%',
              minWidth: 0,
              padding: '0 4px 0 12px',
              borderRadius: UI_TOKENS.radiusPill,
              bgcolor: '#F2F5F1',
              border: '1px solid #C9D7C7',
              fontFamily: UI_TOKENS.fontSans,
              fontSize: 13,
              fontWeight: 400,
              color: UI_TOKENS.textStrong,
            }}
          >
            <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {rule.label}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => onRemoveRule?.(rule.id)}
              aria-label="Remove filter"
              title="Remove filter"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                flexShrink: 0,
                border: 'none',
                borderRadius: '50%',
                background: 'transparent',
                color: UI_TOKENS.textMuted,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(34,3,31,0.06)', color: UI_TOKENS.textStrong },
              }}
            >
              <PhXSvg size={10} />
            </Box>
          </Box>
        ))}

            {activeKeys.map((key) => {
              const filter = byKey[key];
              if (!filter) return null;
              const isRange = filter.kind === 'dateRange';
              const range = filter.range ?? {};
              const has = isRange ? Boolean(range.from || range.to) : filter.selected.length > 0;
              const showSearch = filter.searchable && filter.options.length > 6;
              const options = showSearch
                ? filter.options.filter((option) => matchesSearch(optionQuery, [option.label]))
                : filter.options;
              const datePicker = isRange ? presetsFor(key, range) : null;
              const value = !has
                ? 'Select…'
                : isRange
                ? datePicker?.matched?.label ?? formatRange(range)
                : filter.selected.length <= 2
                ? filter.selected.join(', ')
                : `${filter.selected.slice(0, 2).join(', ')} +${filter.selected.length - 2}`;
              const editing = menu === key;
              return (
                <Box key={key} data-toolbar-menu={key} sx={{ position: compact ? 'static' : 'relative', maxWidth: '100%', minWidth: 0, flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      height: chipHeight,
                      maxWidth: '100%',
                      padding: '0 2px 0 10px',
                      boxSizing: 'border-box',
                      borderRadius: UI_TOKENS.radiusPill,
                      bgcolor: '#F2F5F1',
                      border: `1px solid ${editing ? UI_TOKENS.sage600 : '#C9D7C7'}`,
                      '&:hover': { borderColor: UI_TOKENS.sage600 },
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => openMenu(key)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        height: '100%',
                        minWidth: 0,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontFamily: UI_TOKENS.fontSans,
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box component="span" sx={{ color: UI_TOKENS.textSecondary, flexShrink: 0 }}>
                        {filter.label}
                      </Box>
                      <Box
                        component="span"
                        title={has ? (isRange ? value : filter.selected.join(', ')) : undefined}
                        sx={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 500,
                          color: has ? UI_TOKENS.textStrong : UI_TOKENS.textMuted,
                        }}
                      >
                        {value}
                      </Box>
                      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0, color: UI_TOKENS.textSecondary }}>
                        <PhCaretDownSvg size={10} />
                      </Box>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setMenu(null);
                        onRemoveFilter(key);
                      }}
                      aria-label="Remove filter"
                      title="Remove filter"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: compact ? 26 : 20,
                        height: compact ? 26 : 20,
                        flexShrink: 0,
                        border: 'none',
                        borderRadius: '50%',
                        background: 'transparent',
                        color: UI_TOKENS.textSecondary,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#E4EBE3' },
                      }}
                    >
                      <PhXSvg size={10} />
                    </Box>
                  </Box>

                  {editing && (
                    <Box ref={menuFit.ref} sx={{ ...popoverSx, left: compact ? 12 : 0, width: isRange ? (compact ? 270 : 300) : compact ? 250 : 280, ...menuFit.sx }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', padding: '14px 16px 8px' }}>
                        <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: UI_TOKENS.textStrong }}>
                          {filter.label}
                        </Box>
                        <Box component="span" sx={{ fontSize: 12.5, color: UI_TOKENS.textMuted }}>
                          {isRange ? 'is within' : 'is any of'}
                        </Box>
                      </Box>
                      {isRange && datePicker && (
                        <Box className="yc-scroll" sx={{ minHeight: 0, overflowY: 'auto', padding: '0 6px 6px' }}>
                          {datePicker.presets.map((preset) => {
                            const on = !datePicker.custom && datePicker.matched?.id === preset.id;
                            return (
                              <Box
                                key={preset.id}
                                component="button"
                                type="button"
                                onClick={() => {
                                  setCustomKey(null);
                                  onSetRange?.(key, { from: preset.from, to: preset.to });
                                }}
                                sx={menuItemSx}
                              >
                                <Radio on={on} />
                                <Box component="span" sx={{ flex: 1 }}>
                                  {preset.label}
                                </Box>
                                <Box component="span" sx={{ fontSize: 12, color: UI_TOKENS.textMuted }}>
                                  {`${format(parseISO(preset.from), 'd MMM')} – ${format(parseISO(preset.to), 'd MMM')}`}
                                </Box>
                              </Box>
                            );
                          })}
                          <Box component="button" type="button" onClick={() => setCustomKey(key)} sx={menuItemSx}>
                            <Radio on={datePicker.custom} />
                            <Box component="span" sx={{ flex: 1 }}>
                              Custom range
                            </Box>
                          </Box>
                          {datePicker.custom && (
                            <Box
                              ref={revealRange}
                              sx={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: '10px', padding: '4px 10px 8px' }}
                            >
                              {(['from', 'to'] as const).map((bound) => (
                                <Box key={bound} component="label" sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                                  <Box component="span" sx={{ fontSize: 12, color: UI_TOKENS.textSecondary }}>
                                    {bound === 'from' ? 'From' : 'To'}
                                  </Box>
                                  <Box
                                    component="input"
                                    type="date"
                                    value={range[bound] ?? ''}
                                    min={bound === 'to' ? range.from : undefined}
                                    max={bound === 'from' ? range.to : undefined}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                      onSetRange?.(key, { ...range, [bound]: e.target.value || undefined })
                                    }
                                    sx={dateInputSx}
                                  />
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                      {showSearch && (
                        <Box sx={{ padding: '0 10px 8px' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              height: 30,
                              padding: '0 10px',
                              border: `1px solid ${UI_TOKENS.borderDefault}`,
                              borderRadius: '8px',
                              '&:focus-within': { borderColor: UI_TOKENS.sage600 },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                color: UI_TOKENS.textMuted,
                                '& svg': { width: 14, height: 14 },
                              }}
                            >
                              <MagnifyingGlassSvg />
                            </Box>
                            <Box
                              component="input"
                              autoFocus
                              value={optionQuery}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionQuery(e.target.value)}
                              placeholder={`Search ${filter.label.toLowerCase()}`}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                border: 'none',
                                outline: 'none',
                                fontFamily: UI_TOKENS.fontSans,
                                fontSize: 13,
                                color: UI_TOKENS.textStrong,
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      <Box
                        className="yc-scroll"
                        sx={{ display: isRange ? 'none' : 'block', maxHeight: 252, minHeight: 0, overflowY: 'auto', padding: '0 6px 6px' }}
                      >
                        {options.map((option) => (
                          <Box
                            key={option.label}
                            component="button"
                            type="button"
                            onClick={() => onToggleOption(key, option.label)}
                            sx={menuItemSx}
                          >
                            <Checkbox on={filter.selected.includes(option.label)} />
                            <Box
                              component="span"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {option.label}
                            </Box>
                            <Box component="span" sx={{ fontSize: 12, color: UI_TOKENS.textMuted }}>
                              {option.count}
                            </Box>
                          </Box>
                        ))}
                        {options.length === 0 && (
                          <Box sx={{ padding: '14px 10px', fontSize: 13, color: UI_TOKENS.textMuted }}>
                            No matches
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 16px',
                          borderTop: `1px solid ${UI_TOKENS.divider}`,
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => {
                            setMenu(null);
                            setCustomKey(null);
                            onRemoveFilter(key);
                          }}
                          sx={{
                            border: 'none',
                            background: 'transparent',
                            p: 0,
                            fontFamily: UI_TOKENS.fontSans,
                            fontSize: 13,
                            color: UI_TOKENS.textSecondary,
                            cursor: 'pointer',
                            '&:hover': { color: UI_TOKENS.red600 },
                          }}
                        >
                          Remove filter
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          onClick={closeMenu}
                          sx={{
                            height: 30,
                            padding: '0 16px',
                            border: `1px solid ${UI_TOKENS.sage600}`,
                            borderRadius: UI_TOKENS.radiusPill,
                            bgcolor: UI_TOKENS.sage600,
                            color: '#fff',
                            fontFamily: UI_TOKENS.fontSans,
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#fff', color: UI_TOKENS.sage600 },
                          }}
                        >
                          Done
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}

            {!compact && (
              <Box data-toolbar-menu="__add2" sx={{ position: 'relative', flexShrink: 0 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => openMenu('__add2')}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    height: 26,
                    padding: '0 10px 0 8px',
                    border: 'none',
                    borderRadius: UI_TOKENS.radiusPill,
                    background: 'transparent',
                    fontFamily: UI_TOKENS.fontSans,
                    fontSize: 13,
                    fontWeight: 500,
                    color: UI_TOKENS.sage600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#F2F5F1' },
                  }}
                >
                  <PhPlusSvg size={12} />
                  Add filter
                </Box>
                {menu === '__add2' && addMenu}
              </Box>
            )}
          </Box>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', height: chipHeight }}>
            {filterActions}
            {filterActions && !compact && (
              <Box component="span" sx={{ width: '1px', height: 16, bgcolor: UI_TOKENS.borderDefault, mx: '6px' }} />
            )}
            <Box
              component="button"
              type="button"
              onClick={() => {
                setMenu(null);
                onClearAll();
              }}
              sx={{
                height: chipHeight,
                padding: '0 4px',
                border: 'none',
                background: 'transparent',
                fontFamily: UI_TOKENS.fontSans,
                fontSize: 13,
                color: UI_TOKENS.textSecondary,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': { color: UI_TOKENS.textStrong },
              }}
            >
              Clear
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default TableToolbar;
